import { Link } from "@tanstack/react-router";
import { GiveFeedbackDialog } from "@/components/reviews/GiveFeedbackDialog";
import {
  MyReviewResults,
  MyReviewTasks,
  TeamReviewResults,
} from "@/components/reviews/MyReviewTasks";
import { ReviewCyclesList } from "@/components/reviews/ReviewCyclesList";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { useCompetencyReportScope } from "@/hooks/use-competency-report";
import { useSession } from "@/lib/auth-client";
import { canViewAll } from "@/lib/permissions";

/**
 * Reviews — your own to fill in, and (for admins) the rounds.
 *
 * Reachable only while `app_settings.reviews_enabled` is on (the
 * `requireReviews` route guard), and every `/api/reviews*` route answers 404
 * when it is off. See `docs/performance-reviews-design.md`.
 *
 * An admin's own reviews sit at the top of the same page rather than on a
 * separate one: being HR does not exempt you from your own, and having them
 * anywhere else is how they get forgotten.
 */
export function ReviewsPage() {
  const { data: session } = useSession();
  const isAdmin = canViewAll(session?.user?.role);
  // A pillar head reaches the competency report without being an admin (WI-284).
  const { data: reportScope } = useCompetencyReportScope();

  useSetBreadcrumbs([{ label: "Feedback" }, { label: "Reviews" }]);

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <PageHeader
          title="Reviews"
          description="Reviews you've been asked to complete."
          actions={
            <div className="flex gap-2">
              {/* Reachable independent of any cycle — EPIC-067 WI-243 AC-691. */}
              <GiveFeedbackDialog />
              {reportScope && (
                <Button variant="outline" asChild>
                  <Link to="/dashboard/reviews/competency-report">Competency Report</Link>
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button variant="outline" asChild>
                    <Link to="/dashboard/reviews/forms">Review Forms</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/dashboard/reviews/recognition-report">Recognition Report</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/dashboard/reviews/cycle-feedback">Cycle Feedback</Link>
                  </Button>
                </>
              )}
            </div>
          }
        />
        <MyReviewTasks />
      </div>

      {/*
        Renders nothing (heading included) until a round you were in has closed,
        so it costs an empty page nothing — and the moment there IS a result it
        sits under your outstanding work rather than needing to be hunted for.
      */}
      <MyReviewResults />

      {/* Renders nothing unless you manage someone whose round has closed. */}
      <TeamReviewResults />

      {isAdmin && (
        <div className="space-y-4">
          <div>
            <h2 className="font-semibold text-lg">Review rounds</h2>
            <p className="text-muted-foreground text-sm">
              The period being reviewed, and everyone it covers.
            </p>
          </div>
          <ReviewCyclesList />
        </div>
      )}
    </div>
  );
}
